import React, { useEffect, useState } from "react";
import { useAxiosInstance } from "../../../services/Interceptor";
import AttendeesModal from "../../coach/dashboard/AttendeesModal";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  eventErrorAction,
  eventLoadingAction,
  eventsSearchAction,
} from "../../../store/slices/eventSlice";
import {
  SearchEventsByParams,
  getAttendees,
} from "../../../services/EventsService";
import { useResponsiveHeight } from "../../../hooks/useResponsiveHeight";
import Modal from "../../../components/Modal";
import AthleteScore from "../../../components/athlete-scores/athlete-score";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { getHighSchoolProfile } from "../../../services/HighSchoolService";
import AthleteSearchPanel from "../../common/athletes/AthleteSearchPanel";
import CoachDashboardEvents from "./CoachDashboardEvents";

function UniversityDashboard() {
  const dispatch = useDispatch();
  const authUser = useAuthUser();
  const { user } = authUser;
  const isHighSchoolCoach = user?.role === "highschool";

  const [showResults, setShowResults] = useState(false);
  const [selectedResultAthlete, setSelectedResultAthlete] = useState(null);
  const r_events = useSelector((state) => state.events.events);
  const loadingEvents = useSelector((state) => state.events.loading);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [coachHighSchool, setCoachHighSchool] = useState(undefined);
  const [coachClub, setCoachClub] = useState(undefined);

  const { adjustedHeight } = useResponsiveHeight({
    mobileHeight: 340,
    desktopHeight: 480,
  });
  const apiInstance = useAxiosInstance();

  useEffect(() => {
    if (!isHighSchoolCoach) return;
    (async () => {
      try {
        const response = await getHighSchoolProfile(apiInstance);
        if (response?.club) {
          setCoachClub(response.club);
        } else {
          setCoachHighSchool(response?.highSchool || undefined);
        }
      } catch (err) {
        toast.error(
          err.message || "An error occurred loading high school profile",
        );
      }
    })();
  }, [apiInstance, isHighSchoolCoach]);

  const loadEvents = async (query = "") => {
    dispatch(eventLoadingAction(true));
    try {
      const response = await SearchEventsByParams(apiInstance, query);
      dispatch(eventsSearchAction(response));
    } catch (err) {
      dispatch(eventErrorAction(err.message || "An error occurred"));
    } finally {
      dispatch(eventLoadingAction(false));
    }
  };

  useEffect(() => {
    loadEvents("");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (query) => {
    loadEvents(query);
  };

  const toggleModal = async (event = null) => {
    if (event) {
      setLoadingAttendees(true);
      try {
        const data = await getAttendees(apiInstance, event.id);
        setAttendees(data);
        setSelectedEvent(event);
      } catch (err) {
        toast.error(err.message || "Error loading attendees");
      } finally {
        setLoadingAttendees(false);
      }
    } else {
      setSelectedEvent(null);
      setAttendees([]);
    }
  };

  return (
    <>
      <div className="flex-grow pb-20 xl:pb-0 lg:pb-0">
        <ToastContainer />
        <div className="p-4 h-full bg-gray-100">
          <div className="flex flex-col xl:flex-row lg:flex-row gap-4">
            <div className="w-full lg:w-2/4 xl:w-2/4">
              <div className="p-4 bg-white shadow-lg rounded-lg border-gray-300">
                <h2 className="text-xl mb-5 uppercase">
                  {isHighSchoolCoach ? "Athletes" : "SHARED PROFILE"}
                </h2>
                <AthleteSearchPanel
                  authUser={authUser}
                  adjustedHeight={adjustedHeight + 140}
                  highSchool={isHighSchoolCoach ? coachHighSchool : undefined}
                  club={isHighSchoolCoach ? coachClub : undefined}
                  showFilters
                  listVariant="chips"
                />
              </div>
            </div>

            <div className="w-full lg:w-2/4 xl:w-2/4">
              <CoachDashboardEvents
                loadingEvents={loadingEvents}
                onSearch={handleSearch}
                adjustedHeight={adjustedHeight}
                events={r_events}
                onViewAttendees={toggleModal}
                userRole={user.role}
              />
            </div>
          </div>
        </div>
      </div>

      {selectedEvent && (
        <AttendeesModal
          event={selectedEvent}
          attendees={attendees}
          loading={loadingAttendees}
          onClose={() => toggleModal()}
          setSelectedAthlete={setSelectedAthlete}
          onViewResults={(athlete) => {
            setSelectedResultAthlete(athlete);
            setShowResults(true);
          }}
          userRole={user.role}
        />
      )}

      {selectedAthlete && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedAthlete(null)}
          backdrop="static"
          keyboard={false}
        >
          <AthleteScore
            userId={selectedAthlete.userId}
            showOffers={true}
            showTrackittScore={true}
            showPositionScore={true}
            searchEvent={false}
            showProfile={true}
            showRank={true}
            showLikes={true}
          />
        </Modal>
      )}

      {showResults && selectedResultAthlete && (
        <Modal
          isOpen={true}
          onClose={() => {
            setShowResults(false);
            setSelectedResultAthlete(null);
          }}
          backdrop="static"
          keyboard={false}
        >
          <h2 className="text-xl p-4 mt-1 font-semibold">
            Results for {selectedResultAthlete.name} - {selectedEvent.name}
          </h2>
          <AthleteScore
            key={`coach-${selectedResultAthlete?.userId}`}
            userId={selectedResultAthlete.userId}
            showOffers={false}
            showTrackittScore={true}
            showPositionScore={true}
            searchEvent={false}
            showProfile={false}
            showRank={true}
            showLikes={false}
            eventId={selectedEvent.id}
            sharedProfile={true}
          />
        </Modal>
      )}
    </>
  );
}

export default UniversityDashboard;
